import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { useAuthContext } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Users,
  Activity,
  TrendingUp,
  Calendar,
  Copy,
  Check,
  X,
  Clock,
  Mail,
  Lock,
  Unlock,
  Plus,
  Trash2,
  CirclePause,
  CircleCheck,
  ShieldCheck,
} from "lucide-react";

type ManagedUser = {
  id: number;
  name: string | null;
  email: string | null;
  role: "admin" | "user";
  accessStatus: "active" | "suspended";
  accessReason: string | null;
};

function StatCard({
  title,
  value,
  icon: Icon,
  color,
}: {
  title: string;
  value: number | string;
  icon: React.ElementType;
  color: string;
}) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
          </div>
          <div className={`p-3 rounded-full ${color}`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={copy} className="text-muted-foreground hover:text-foreground transition-colors ml-1">
      {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

function formatDate(ts: number) {
  return new Date(ts).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function timeAgo(ts: number) {
  const diff = Date.now() - ts;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return "hoje";
  if (days === 1) return "ontem";
  return `${days} dias atrás`;
}

export default function Admin() {
  const { user, loading: authLoading } = useAuthContext();
  const [betaDate, setBetaDate] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newNote, setNewNote] = useState("");
  const [maxSlotsInput, setMaxSlotsInput] = useState("");
  const [emailsCopied, setEmailsCopied] = useState(false);
  const [managedUser, setManagedUser] = useState<ManagedUser | null>(null);
  const [suspensionReason, setSuspensionReason] = useState("Pagamento pendente");

  const statsQuery = trpc.admin.getStats.useQuery(undefined, { enabled: user?.role === "admin" });
  const usersQuery = trpc.admin.getUsers.useQuery(undefined, { enabled: user?.role === "admin" });
  const betaQuery = trpc.admin.getBetaExpiry.useQuery(undefined, { enabled: user?.role === "admin" });
  const interestsQuery = trpc.admin.getBetaInterests.useQuery(undefined, { enabled: user?.role === "admin" });
  const inviteModeQuery = trpc.admin.getInviteMode.useQuery(undefined, { enabled: user?.role === "admin" });
  const allowedEmailsQuery = trpc.admin.getAllowedEmails.useQuery(undefined, { enabled: user?.role === "admin" });
  const maxSlotsQuery = trpc.admin.getInviteMaxSlots.useQuery(undefined, { enabled: user?.role === "admin" });

  const utils = trpc.useUtils();
  const setBetaMutation = trpc.admin.setBetaExpiry.useMutation({
    onSuccess: () => {
      utils.admin.getStats.invalidate();
      utils.admin.getBetaExpiry.invalidate();
      toast.success("Data de encerramento atualizada!");
    },
    onError: (e) => toast.error(e.message),
  });

  const setInviteModeMutation = trpc.admin.setInviteMode.useMutation({
    onSuccess: () => {
      utils.admin.getInviteMode.invalidate();
      toast.success(inviteModeQuery.data?.enabled ? "Modo aberto ativado." : "Modo restrito ativado.");
    },
    onError: (e) => toast.error(e.message),
  });

  const addEmailMutation = trpc.admin.addAllowedEmail.useMutation({
    onSuccess: (data) => {
      utils.admin.getAllowedEmails.invalidate();
      utils.admin.getInviteMaxSlots.invalidate();
      setNewEmail("");
      setNewNote("");
      if (data.emailSent) {
        toast.success("Testador adicionado! E-mail de boas-vindas enviado.");
      } else {
        toast.success("Testador adicionado. (E-mail de boas-vindas não enviado — verifique a configuração do Resend.)");
      }
    },
    onError: (e) => toast.error(e.message),
  });

  const setMaxSlotsMutation = trpc.admin.setInviteMaxSlots.useMutation({
    onSuccess: () => {
      utils.admin.getInviteMaxSlots.invalidate();
      toast.success("Limite de vagas atualizado!");
      setMaxSlotsInput("");
    },
    onError: (e) => toast.error(e.message),
  });

  const setUserAccessMutation = trpc.admin.setUserAccess.useMutation({
    onSuccess: () => {
      utils.admin.getUsers.invalidate();
      utils.admin.getStats.invalidate();
      const restored = managedUser?.accessStatus === "suspended";
      toast.success(restored ? "Acesso reativado." : "Conta suspensa. Os dados foram preservados.");
      setManagedUser(null);
      setSuspensionReason("Pagamento pendente");
    },
    onError: (e) => toast.error(e.message),
  });

  const recordPaymentMutation = trpc.admin.recordUserPayment.useMutation({
    onSuccess: (data) => {
      utils.admin.getUsers.invalidate();
      utils.admin.getStats.invalidate();
      const due = new Date(data.nextBillingDueAt).toLocaleDateString("pt-BR");
      toast.success(`Pagamento registrado. Próximo vencimento: ${due}.`);
    },
    onError: (e) => toast.error(e.message),
  });

  const handleCopyEmails = () => {
    const emails = (allowedEmailsQuery.data ?? []).map((e) => e.email).join(", ");
    navigator.clipboard.writeText(emails).then(() => {
      setEmailsCopied(true);
      setTimeout(() => setEmailsCopied(false), 2000);
    });
  };

  const removeEmailMutation = trpc.admin.removeAllowedEmail.useMutation({
    onSuccess: () => {
      utils.admin.getAllowedEmails.invalidate();
      toast.success("E-mail removido.");
    },
    onError: (e) => toast.error(e.message),
  });

  const resendInviteMutation = trpc.admin.resendWelcomeTesterEmail.useMutation({
    onSuccess: () => toast.success("Convite reenviado. Peça ao testador para verificar também a caixa de spam."),
    onError: (e) => toast.error(e.message),
  });

  if (authLoading) return <div className="flex items-center justify-center h-screen">Carregando...</div>;
  if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const stats = statsQuery.data;
  const users = usersQuery.data ?? [];
  const betaExpiry = betaQuery.data?.betaExpiry;
  const interests = interestsQuery.data ?? [];

  const handleSetBeta = () => {
    if (!betaDate) return;
    setBetaMutation.mutate({ date: new Date(betaDate).toISOString() });
  };

  const handleClearBeta = () => {
    setBetaMutation.mutate({ date: null });
    setBetaDate("");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Painel de Administração</h1>
            <p className="text-sm text-muted-foreground">Controle de usuários, pagamentos mensais, suspensões e convites</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => window.history.back()}>
            ← Voltar
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          <strong>Como controlar mensalidades:</strong> depois de confirmar um pagamento fora do Bolso, clique em <strong>Pago +30d</strong> na linha do usuário. Se não houver pagamento, use <strong>Suspender</strong>. O bloqueio preserva todos os dados e pode ser revertido em <strong>Reativar</strong>. Este painel faz o controle manual; ele não cobra cartão nem recebe pagamentos automaticamente.
        </div>

        {/* Stats */}
        <section>
          <h2 className="text-base font-semibold mb-4 text-muted-foreground uppercase tracking-wide text-xs">
            Resumo geral
          </h2>
          {statsQuery.isLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <StatCard title="Total de usuários" value={stats?.totalUsers ?? 0} icon={Users} color="bg-blue-500" />
              <StatCard title="Ativos (7 dias)" value={stats?.activeUsers ?? 0} icon={Activity} color="bg-green-500" />
              <StatCard title="Inativos" value={stats?.inactiveUsers ?? 0} icon={Clock} color="bg-amber-500" />
              <StatCard title="Total de lançamentos" value={stats?.totalTransactions ?? 0} icon={TrendingUp} color="bg-purple-500" />
              <StatCard title="Contas suspensas" value={stats?.suspendedUsers ?? 0} icon={CirclePause} color="bg-red-500" />
            </div>
          )}
        </section>

        {/* Beta Expiry */}
        <section>
          <h2 className="text-base font-semibold mb-4 text-muted-foreground uppercase tracking-wide text-xs">
            Encerramento do período de testes
          </h2>
          <Card>
            <CardContent className="p-5 space-y-4">
              {betaExpiry ? (
                <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <Calendar className="w-4 h-4 text-amber-600 shrink-0" />
                  <p className="text-sm text-amber-800">
                    O beta encerrará em{" "}
                    <strong>{new Date(betaExpiry).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })}</strong>.
                    Após essa data, usuários comuns verão a tela de encerramento.
                  </p>
                  <Button variant="ghost" size="sm" className="ml-auto text-red-600 hover:text-red-700" onClick={handleClearBeta}>
                    <X className="w-4 h-4 mr-1" /> Remover
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Nenhuma data de encerramento definida. O app está aberto para todos os usuários.
                </p>
              )}
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <label className="text-sm font-medium mb-1 block">
                    {betaExpiry ? "Alterar data de encerramento" : "Definir data de encerramento"}
                  </label>
                  <Input
                    type="date"
                    value={betaDate}
                    onChange={(e) => setBetaDate(e.target.value)}
                    min={new Date().toISOString().split("T")[0]}
                  />
                </div>
                <Button
                  onClick={handleSetBeta}
                  disabled={!betaDate || setBetaMutation.isPending}
                  className="bg-[#1a4731] hover:bg-[#1a4731]/90"
                >
                  {setBetaMutation.isPending ? "Salvando..." : "Salvar data"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Users Table */}
        <section>
          <h2 className="text-base font-semibold mb-4 text-muted-foreground uppercase tracking-wide text-xs">
            Usuários cadastrados ({users.length})
          </h2>
          <Card>
            <CardContent className="p-0">
              {usersQuery.isLoading ? (
                <div className="p-5 space-y-3">
                  {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 rounded" />)}
                </div>
              ) : users.length === 0 ? (
                <p className="p-5 text-sm text-muted-foreground">Nenhum usuário cadastrado ainda.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/40">
                        <th className="text-left px-4 py-3 font-medium text-muted-foreground">Usuário</th>
                        <th className="text-left px-4 py-3 font-medium text-muted-foreground">E-mail</th>
                        <th className="text-center px-4 py-3 font-medium text-muted-foreground">Lançamentos</th>
                        <th className="text-left px-4 py-3 font-medium text-muted-foreground">Último acesso</th>
                        <th className="text-left px-4 py-3 font-medium text-muted-foreground">Cadastro</th>
                        <th className="text-left px-4 py-3 font-medium text-muted-foreground">Mensalidade</th>
                        <th className="text-center px-4 py-3 font-medium text-muted-foreground">Acesso</th>
                        <th className="text-right px-4 py-3 font-medium text-muted-foreground">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.id} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-[#1a4731]/10 flex items-center justify-center text-xs font-semibold text-[#1a4731]">
                                {(u.name ?? u.email ?? "?")[0].toUpperCase()}
                              </div>
                              <span className="font-medium truncate max-w-[120px]">{u.name ?? "—"}</span>
                              {u.role === "admin" && (
                                <Badge variant="secondary" className="text-xs px-1.5 py-0">admin</Badge>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">
                            <span className="flex items-center gap-1">
                              {u.email ?? "—"}
                              {u.email && <CopyButton text={u.email} />}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`font-semibold ${u.transactionCount === 0 ? "text-muted-foreground" : "text-[#1a4731]"}`}>
                              {u.transactionCount}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground text-xs">{timeAgo(u.lastSignedIn)}</td>
                          <td className="px-4 py-3 text-muted-foreground text-xs">{formatDate(u.createdAt)}</td>
                          <td className="px-4 py-3 text-xs">
                            {u.role === "admin" ? (
                              <span className="text-muted-foreground">Administrador</span>
                            ) : u.nextBillingDueAt ? (
                              <div>
                                <p className={u.nextBillingDueAt < Date.now() ? "text-destructive font-medium" : "text-foreground font-medium"}>
                                  Vence {formatDate(u.nextBillingDueAt)}
                                </p>
                                {u.lastPaymentAt && <p className="text-muted-foreground">Pago em {formatDate(u.lastPaymentAt)}</p>}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">Não iniciado</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge
                              variant={u.accessStatus === "active" ? "default" : "secondary"}
                              className={u.accessStatus === "active" ? "bg-green-100 text-green-700 border-green-200" : "bg-red-100 text-red-700 border-red-200"}
                            >
                              {u.accessStatus === "active" ? "Ativo" : "Suspenso"}
                            </Badge>
                            {u.accessReason && <p className="mt-1 text-[10px] text-muted-foreground max-w-28 mx-auto">{u.accessReason}</p>}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {u.role !== "admin" && (
                              <div className="flex justify-end gap-1.5">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8 text-xs"
                                  onClick={() => recordPaymentMutation.mutate({ userId: u.id })}
                                  disabled={recordPaymentMutation.isPending}
                                >
                                  <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Pago +30d
                                </Button>
                                <Button
                                  size="sm"
                                  variant={u.accessStatus === "suspended" ? "outline" : "destructive"}
                                  className="h-8 text-xs"
                                  onClick={() => {
                                    setManagedUser(u);
                                    setSuspensionReason(u.accessReason ?? "Pagamento pendente");
                                  }}
                                >
                                  {u.accessStatus === "suspended" ? <CircleCheck className="w-3.5 h-3.5 mr-1" /> : <CirclePause className="w-3.5 h-3.5 mr-1" />}
                                  {u.accessStatus === "suspended" ? "Reativar" : "Suspender"}
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </section>

        {/* Testadores — Invite-only mode */}
        <section>
          <h2 className="text-base font-semibold mb-4 text-muted-foreground uppercase tracking-wide text-xs">
            Acesso por convite
          </h2>
          <Card>
            <CardContent className="p-5 space-y-5">
              {/* Toggle invite-only */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">
                    {inviteModeQuery.data?.enabled ? (
                      <span className="flex items-center gap-1.5 text-amber-700">
                        <Lock className="w-4 h-4" /> Modo restrito ativo
                      </span>
                    ) : (
                      <span className="flex items-center gap-1.5 text-green-700">
                        <Unlock className="w-4 h-4" /> Acesso aberto
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {inviteModeQuery.data?.enabled
                      ? "Apenas e-mails da lista abaixo podem entrar."
                      : "Qualquer pessoa pode criar conta e entrar."}
                  </p>
                </div>
                <Button
                  variant={inviteModeQuery.data?.enabled ? "outline" : "default"}
                  size="sm"
                  className={inviteModeQuery.data?.enabled ? "" : "bg-[#1a4731] hover:bg-[#1a4731]/90"}
                  onClick={() => setInviteModeMutation.mutate({ enabled: !inviteModeQuery.data?.enabled })}
                  disabled={setInviteModeMutation.isPending}
                >
                  {inviteModeQuery.data?.enabled ? "Abrir acesso" : "Restringir acesso"}
                </Button>
              </div>

              {/* Limite de vagas */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-medium">Limite de vagas</p>
                    <p className="text-xs text-muted-foreground">
                      {maxSlotsQuery.data?.maxSlots
                        ? `${(allowedEmailsQuery.data ?? []).length} / ${maxSlotsQuery.data.maxSlots} vagas usadas`
                        : `${(allowedEmailsQuery.data ?? []).length} testadores — sem limite`}
                    </p>
                  </div>
                  <div className="flex gap-2 items-center">
                    <Input
                      type="number"
                      min="1"
                      placeholder="Máx. vagas"
                      value={maxSlotsInput}
                      onChange={(e) => setMaxSlotsInput(e.target.value)}
                      className="w-28 text-sm"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setMaxSlotsMutation.mutate({ maxSlots: maxSlotsInput ? parseInt(maxSlotsInput) : null })}
                      disabled={setMaxSlotsMutation.isPending}
                    >
                      Salvar
                    </Button>
                    {maxSlotsQuery.data?.maxSlots && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setMaxSlotsMutation.mutate({ maxSlots: null })}
                        disabled={setMaxSlotsMutation.isPending}
                        className="text-muted-foreground"
                      >
                        Remover limite
                      </Button>
                    )}
                  </div>
                </div>
                {/* Progress bar when limit is set */}
                {maxSlotsQuery.data?.maxSlots && (
                  <div className="w-full bg-muted rounded-full h-1.5">
                    <div
                      className="bg-[#166534] h-1.5 rounded-full transition-all"
                      style={{ width: `${Math.min(100, ((allowedEmailsQuery.data ?? []).length / maxSlotsQuery.data.maxSlots) * 100)}%` }}
                    />
                  </div>
                )}
              </div>

              {/* Add email form */}
              <div className="border-t pt-4">
                <p className="text-sm font-medium mb-3">Adicionar testador</p>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="email@exemplo.com"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && newEmail) {
                        addEmailMutation.mutate({ email: newEmail, note: newNote || undefined, siteUrl: window.location.origin });
                      }
                    }}
                    className="flex-1"
                  />
                  <Input
                    placeholder="Observação (opcional)"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    onClick={() => addEmailMutation.mutate({ email: newEmail, note: newNote || undefined, siteUrl: window.location.origin })}
                    disabled={!newEmail || addEmailMutation.isPending}
                    className="bg-[#1a4731] hover:bg-[#1a4731]/90 shrink-0"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Email list */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-medium">Lista de testadores autorizados</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Estar nesta lista não cria a conta. O testador ainda precisa acessar o Bolso e entrar com este e-mail.
                    </p>
                  </div>
                  {(allowedEmailsQuery.data ?? []).length > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCopyEmails}
                      className="gap-1.5 text-xs"
                    >
                      {emailsCopied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                      {emailsCopied ? "Copiado!" : "Copiar todos"}
                    </Button>
                  )}
                </div>
                {allowedEmailsQuery.isLoading ? (
                  <div className="space-y-2">
                    {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-9 rounded" />)}
                  </div>
                ) : (allowedEmailsQuery.data ?? []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nenhum e-mail na lista ainda.</p>
                ) : (
                  <div className="space-y-2">
                    {(allowedEmailsQuery.data ?? []).map((item) => (
                      <div key={item.id} className="flex items-center justify-between gap-3 py-2 px-3 bg-muted/40 rounded-lg">
                        <div className="flex items-center gap-2 min-w-0">
                          <Mail className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                          <span className="text-sm font-medium truncate">{item.email}</span>
                          <Badge variant={item.registered ? "secondary" : "outline"} className="text-[11px] shrink-0">
                            {item.registered ? "Cadastrado" : "Aguardando acesso"}
                          </Badge>
                          {item.note && (
                            <span className="text-xs text-muted-foreground truncate">— {item.note}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs h-8"
                            onClick={() => resendInviteMutation.mutate({ email: item.email, siteUrl: window.location.origin })}
                            disabled={resendInviteMutation.isPending}
                          >
                            Reenviar convite
                          </Button>
                          <button
                            onClick={() => removeEmailMutation.mutate({ id: item.id })}
                            disabled={removeEmailMutation.isPending}
                            aria-label={`Remover ${item.email}`}
                            className="text-muted-foreground hover:text-red-600 transition-colors shrink-0"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Beta Interests */}
        {interests.length > 0 && (
          <section>
            <h2 className="text-base font-semibold mb-4 text-muted-foreground uppercase tracking-wide text-xs">
              Interessados no lançamento ({interests.length})
            </h2>
            <Card>
              <CardContent className="p-5">
                <div className="flex flex-wrap gap-2">
                  {interests.map((i) => (
                    <div key={i.id} className="flex items-center gap-1.5 bg-muted px-3 py-1.5 rounded-full text-sm">
                      <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                      {i.email}
                      <CopyButton text={i.email} />
                    </div>
                  ))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={() => {
                    const emails = interests.map((i) => i.email).join(", ");
                    navigator.clipboard.writeText(emails);
                    toast.success("Todos os e-mails copiados!");
                  }}
                >
                  <Copy className="w-3.5 h-3.5 mr-1.5" />
                  Copiar todos os e-mails
                </Button>
              </CardContent>
            </Card>
          </section>
        )}
      </div>

      <Dialog open={!!managedUser} onOpenChange={(open) => !open && setManagedUser(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {managedUser?.accessStatus === "suspended" ? "Reativar acesso" : "Suspender acesso"}
            </DialogTitle>
            <DialogDescription>
              {managedUser?.accessStatus === "suspended"
                ? `A conta de ${managedUser.name ?? managedUser.email ?? "usuário"} voltará a usar o Bolso imediatamente.`
                : `A conta de ${managedUser?.name ?? managedUser?.email ?? "usuário"} deixará de usar o aplicativo e o WhatsApp até ser reativada. Os dados não serão apagados.`}
            </DialogDescription>
          </DialogHeader>

          {managedUser?.accessStatus !== "suspended" && (
            <div className="space-y-2">
              <Label htmlFor="suspension-reason">Motivo interno</Label>
              <Input
                id="suspension-reason"
                value={suspensionReason}
                onChange={(e) => setSuspensionReason(e.target.value)}
                maxLength={200}
                placeholder="Ex.: Pagamento pendente"
              />
              <p className="text-xs text-muted-foreground">O motivo fica visível apenas neste painel.</p>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setManagedUser(null)}>Cancelar</Button>
            <Button
              variant={managedUser?.accessStatus === "suspended" ? "default" : "destructive"}
              onClick={() => {
                if (!managedUser) return;
                setUserAccessMutation.mutate({
                  userId: managedUser.id,
                  status: managedUser.accessStatus === "suspended" ? "active" : "suspended",
                  reason: managedUser.accessStatus === "suspended" ? undefined : suspensionReason,
                });
              }}
              disabled={setUserAccessMutation.isPending}
            >
              {setUserAccessMutation.isPending ? "Salvando..." : managedUser?.accessStatus === "suspended" ? "Reativar acesso" : "Suspender agora"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
